import { useState } from "react";
import { MadeWithViny } from "../MadeWithViny";
import { Disc3, Heart, MapPin } from "lucide-react";
import { VerifiedBadge } from "@/components/ui/verified-badge";
import type { TemplateProps, AlbumItem } from "../types";
import { AlbumDetailDrawer } from "../AlbumDetailDrawer";

/* ─────────────────────────────────────────────────────────────
   Colecionador — sidebar fixa com perfil + bio + gêneros à
   esquerda, e paredes editoriais separadas para Coleção e
   Lista de interesse à direita.
   ───────────────────────────────────────────────────────────── */

function Cover({ a }: { a: AlbumItem }) {
  return a.cover_url ? (
    <img
      src={a.cover_url}
      alt={`${a.title} — ${a.artist}`}
      loading="lazy"
      className="h-full w-full object-cover"
    />
  ) : (
    <div className="grid h-full w-full place-items-center bg-neutral-200 text-neutral-400">
      <Disc3 className="h-1/3 w-1/3" />
    </div>
  );
}

function Wall({
  title,
  icon,
  items,
  onSelect,
}: {
  title: string;
  icon: React.ReactNode;
  items: AlbumItem[];
  onSelect: (a: AlbumItem) => void;
}) {
  if (!items.length) return null;
  return (
    <section className="mt-2 first:mt-0">
      <div className="mb-5 flex items-baseline gap-3 border-b border-neutral-900/10 pb-3">
        <h2 className="inline-flex items-center gap-2 font-display text-xl font-medium tracking-tight text-neutral-900">
          {icon} {title}
        </h2>
        <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">
          {items.length} {items.length === 1 ? "item" : "itens"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {items.slice(0, 60).map((a) => (
          <button
            type="button"
            key={a.id}
            onClick={() => onSelect(a)}
            className="group text-left"
          >
            <div className="relative aspect-square overflow-hidden rounded-md bg-neutral-200 ring-1 ring-neutral-900/5 transition-transform group-hover:-translate-y-0.5">
              <Cover a={a} />
            </div>
            <figcaption className="mt-2.5">
              <div className="truncate text-[13px] font-medium text-neutral-900">
                {a.title}
              </div>
              <div className="truncate text-[11px] text-neutral-500">{a.artist}</div>
            </figcaption>
          </button>
        ))}
      </div>
    </section>
  );
}

export function CuradoriaTemplate({ bio, config }: TemplateProps) {
  const [selected, setSelected] = useState<AlbumItem | null>(null);
  const name = config.title || bio.display_name || bio.username;
  const showCollection = config.sections.collection !== false;
  const showWishlist = config.sections.wishlist !== false;
  const collection = showCollection ? bio.owned : [];
  const wishlist = showWishlist ? bio.wishlist : [];
  const tagline = config.subtitle || bio.bio;
  const total = collection.length + wishlist.length;
  const avatar =
    bio.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${bio.username}`;

  return (
    <div className="min-h-screen bg-[#f4f2ee] text-neutral-900">
      <div className="lg:grid lg:grid-cols-[320px_1fr] lg:gap-0">
        {/* SIDEBAR (sticky no desktop) */}
        <aside className="px-8 pt-12 lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:justify-between lg:py-12">
          <div>
            <div className="flex items-center gap-3">
              <img loading="lazy" decoding="async"
                src={avatar}
                alt={name}
                className="h-12 w-12 rounded-full object-cover ring-1 ring-neutral-900/10"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 truncate font-display text-base font-semibold">
                  {name}
                  {bio.is_premium && (
                    <VerifiedBadge variant="shimmer" size={16} aria-label="Verificado" />
                  )}
                </div>
                <div className="truncate text-xs text-neutral-500">@{bio.username}</div>
              </div>
            </div>

            <h1
              className="mt-10 font-display font-medium tracking-tight"
              style={{ fontSize: "clamp(2.5rem, 4.4vw, 4rem)", lineHeight: 0.95 }}
            >
              Coleção
            </h1>

            {tagline && (
              <p className="mt-6 max-w-xs text-sm leading-relaxed text-neutral-600">
                {tagline}
              </p>
            )}

            {(bio.city || bio.country) && (
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-neutral-500">
                <MapPin className="h-3.5 w-3.5" />
                {[bio.city, bio.country].filter(Boolean).join(", ")}
              </div>
            )}

            {bio.favorite_genres.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-1.5">
                {bio.favorite_genres.map((g) => (
                  <span
                    key={g}
                    className="rounded-full border border-neutral-900/15 px-3 py-1 text-[11px] font-medium text-neutral-700"
                  >
                    {g}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8 flex gap-6 text-xs text-neutral-600">
              <div>
                <div className="font-display text-2xl font-medium text-neutral-900">
                  {collection.length}
                </div>
                <div className="uppercase tracking-[0.18em] text-neutral-500">Coleção</div>
              </div>
              <div>
                <div className="font-display text-2xl font-medium text-neutral-900">
                  {wishlist.length}
                </div>
                <div className="uppercase tracking-[0.18em] text-neutral-500">Interesse</div>
              </div>
            </div>
          </div>

          <div className="mt-12 hidden lg:block">
            <MadeWithViny />
          </div>
        </aside>

        {/* PAREDES editoriais separadas */}
        <main className="space-y-16 px-8 py-12 lg:py-14 lg:pr-12">
          {total === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed border-neutral-300 py-32 text-sm text-neutral-500">
              A coleção aparecerá aqui.
            </div>
          ) : (
            <>
              <Wall
                title="Coleção"
                icon={<Disc3 className="h-4 w-4" />}
                items={collection}
                onSelect={setSelected}
              />
              <Wall
                title="Lista de interesse"
                icon={<Heart className="h-4 w-4" />}
                items={wishlist}
                onSelect={setSelected}
              />
            </>
          )}
        </main>
      </div>

      <footer className="flex items-center justify-between border-t border-neutral-900/10 px-8 py-5 text-[10px] uppercase tracking-[0.22em] text-neutral-500 lg:hidden">
        <MadeWithViny />
        <span>{total} itens</span>
      </footer>

      <AlbumDetailDrawer
        album={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </div>
  );
}
